import { writable } from 'svelte/store';
import {
    getBillGovernments,
    getBillSessions,
    getBillSpotlights,
    searchBills,
    type BillGovernment,
    type BillPositionFilter,
    type BillSearchItem,
    type BillSession,
    type BillSpotlight
} from '../../api/bills';

export interface BillsContext {
    spotlights: BillSpotlight[];
    governments: BillGovernment[];
}

let contextPromise: Promise<BillsContext> | null = null;

/** Key legislation + jurisdictions, fetched once per page load and shared by every route. */
export function loadBillsContext(): Promise<BillsContext> {
    contextPromise ??= Promise.all([getBillSpotlights(), getBillGovernments()])
        .then(([spotlights, governments]) => ({ spotlights, governments }))
        .catch((e) => {
            contextPromise = null;
            throw e;
        });
    return contextPromise;
}

const sessionsByGovernment = new Map<string, Promise<BillSession[]>>();

function loadSessions(governmentUri: string): Promise<BillSession[]> {
    let sessions = sessionsByGovernment.get(governmentUri);
    if (!sessions) {
        sessions = getBillSessions(governmentUri).catch((e) => {
            sessionsByGovernment.delete(governmentUri);
            throw e;
        });
        sessionsByGovernment.set(governmentUri, sessions);
    }
    return sessions;
}

export interface BillSearchState {
    /** Keywords of the submitted search; empty when no search has run. */
    keywords: string;
    governmentUri: string;
    items: BillSearchItem[];
    sessionNames: string[];
    totalResults: number;
    continuationToken: string | null;
    loading: boolean;
    loadingMore: boolean;
    error: boolean;
}

const emptySearch: BillSearchState = {
    keywords: '',
    governmentUri: '',
    items: [],
    sessionNames: [],
    totalResults: 0,
    continuationToken: null,
    loading: false,
    loadingMore: false,
    error: false
};

/**
 * Filters kept across navigation so "Back to bills" returns to the same view.
 * Search and key legislation each have their own jurisdiction (legacy parity).
 */
export const billsFilters = writable<{
    searchGovernmentUri: string;
    keyLegislationGovernmentUri: string;
    category: string;
    position: BillPositionFilter;
    initialized: boolean;
}>({ searchGovernmentUri: '', keyLegislationGovernmentUri: '', category: '', position: 'all', initialized: false });

export const billSearch = writable<BillSearchState>(emptySearch);

/** List scroll position, restored when coming back from a bill's page. */
export const billsListScroll = { y: 0 };

/** Set while a bill's page is open, so an official's profile opened from it can offer "Back to bill". */
export const billsLastBillUri = { value: '' };

/** Bill URLs in the order the list last showed them, so a bill's page can step to the previous/next one. */
export const billsListedUrls = writable<string[]>([]);

let searchSequence = 0;

/** Runs a new search across every active session of the jurisdiction (legacy behavior). */
export async function runBillSearch(governmentUri: string, keywords: string): Promise<void> {
    const sequence = ++searchSequence;
    if (!keywords) {
        billSearch.set(emptySearch);
        return;
    }
    billSearch.set({ ...emptySearch, keywords, governmentUri, loading: true });
    try {
        const sessions = await loadSessions(governmentUri);
        const page = await searchBills(governmentUri, sessions, keywords);
        if (sequence !== searchSequence) return;
        billSearch.set({
            ...emptySearch,
            keywords,
            governmentUri,
            items: page.proposals,
            sessionNames: page.sessionNames,
            totalResults: page.totalResults,
            continuationToken: page.continuationToken
        });
    } catch (e) {
        if (sequence !== searchSequence) return;
        console.error('[Bills] Search failed:', e);
        billSearch.set({ ...emptySearch, keywords, governmentUri, error: true });
    }
}

/** Appends the next page of the current search ("Show more"). */
export async function loadMoreBillSearchResults(current: BillSearchState): Promise<void> {
    if (!current.continuationToken || current.loadingMore) return;
    const sequence = searchSequence;
    billSearch.set({ ...current, loadingMore: true });
    try {
        const sessions = await loadSessions(current.governmentUri);
        const page = await searchBills(current.governmentUri, sessions, current.keywords, current.continuationToken);
        if (sequence !== searchSequence) return;
        billSearch.set({
            ...current,
            items: [...current.items, ...page.proposals],
            totalResults: page.totalResults || current.totalResults,
            continuationToken: page.continuationToken,
            loadingMore: false
        });
    } catch (e) {
        if (sequence !== searchSequence) return;
        console.error('[Bills] Loading more results failed:', e);
        billSearch.set({ ...current, loadingMore: false, error: true });
    }
}
